import React, { useState } from 'react';
import { Flex } from '@components/Flex';
import {
  NodesContainer,
  StyledButton,
  StyledGradient,
  StyledSection,
  StyledTitle,
  ReviewsContainer,
  ReviewCard,
  ReviewHeader,
  ReviewerName,
  ReviewerLocation,
  ReviewStars,
  ReviewContent,
} from './SEOContentSection.styles';
import { Text } from '@components/typography';
import { JoykitColor } from '@components/themes';

interface NodeType
  extends Readonly<{
    title: string;
    content: string[];
    reviews?: ReadonlyArray<ReviewType>;
  }> {}

interface ReviewType
  extends Readonly<{
    name: string;
    location: string;
    content: string;
  }> {}

interface SEOContentSectionProps
  extends Readonly<{
    id: string;
    ctaLabel: string;
    nodes: ReadonlyArray<NodeType>;
    reviews?: ReadonlyArray<ReviewType>;
    backgroundColor?: JoykitColor;
  }> {}

export const SEOContentSection: React.FC<SEOContentSectionProps> = ({ id, ctaLabel, nodes, reviews, backgroundColor }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Ensure reviews is an array before rendering
  const reviewsArray = Array.isArray(reviews) ? reviews : [];

  return (
    <Flex flexDirection="column" alignItems="center" backgroundColor={backgroundColor}>
      {reviewsArray.length > 0 && (
        <StyledSection as="section" paddingBottom={8}>
          <ReviewsContainer>
            {reviewsArray.map((review, index) => (
              <ReviewCard key={`${review.name}-${review.location}-${index}`}>
                <ReviewHeader>
                  <ReviewerName variant="featureCopy17" tagName="div" color="mono1">
                    <span>{review.name}</span>
                  </ReviewerName>
                  <ReviewerLocation variant="featureCopy17" tagName="div" color="mono1">
                    {review.location}
                  </ReviewerLocation>
                  <ReviewStars>
                    <span>★★★★★</span>
                  </ReviewStars>
                </ReviewHeader>
                <ReviewContent variant="featureCopy17" tagName="div" color="mono1">
                  <span>{review.content}</span>
                </ReviewContent>
              </ReviewCard>
            ))}
          </ReviewsContainer>
        </StyledSection>
      )}
      <StyledSection as="section" id={id} paddingBottom="82px">
        <NodesContainer style={{ maxHeight: isExpanded ? 'none' : '300px', overflow: 'hidden' }}>
          {nodes.map((node) => (
            <React.Fragment key={node.title}>
              <StyledTitle variant="featureCopy17" tagName="h4" color="mono1">
                {node.title}
              </StyledTitle>
              {Array.isArray(node.content) &&
                node.content.length > 0 &&
                node.content.map((content, contentIndex) => (
                  <Text variant="featureCopy17" tagName="p" key={`${node.title}-content-${contentIndex}`} color="mono1">
                    {content}
                  </Text>
                ))}
              {node.reviews && Array.isArray(node.reviews) && node.reviews.length > 0 && (
                <ReviewsContainer>
                  {node.reviews.map((review, index) => (
                    <ReviewCard key={`${review.name}-${review.location}-${index}`}>
                      <ReviewHeader>
                        <ReviewerName variant="featureCopy17" tagName="div" color="mono1">
                          <span>{review.name}</span>
                        </ReviewerName>
                        <ReviewerLocation variant="featureCopy17" tagName="div" color="mono1">
                          {review.location}
                        </ReviewerLocation>
                      </ReviewHeader>
                      <ReviewContent variant="featureCopy17" tagName="div" color="mono1">
                        <span>{review.content}</span>
                      </ReviewContent>
                    </ReviewCard>
                  ))}
                </ReviewsContainer>
              )}
            </React.Fragment>
          ))}
          {!isExpanded && <StyledGradient gradientColor={backgroundColor} />}
        </NodesContainer>
        {!isExpanded && (
          <StyledButton id="read-more" onClick={() => setIsExpanded(!isExpanded)}>
            <Text variant="featureCopy17" tagName="span" color="mono1">
              {ctaLabel}
            </Text>
          </StyledButton>
        )}
      </StyledSection>
    </Flex>
  );
};
